import { ProductCard } from '../../../../productFilter/products';
import {
  CardAnchor,
  Image,
  Title,
  CardButton,
  TopicsList,
  Badge,
  SponsorsList,
  TextListSpacer,
  Date,
  CardAnchorProps,
} from '../../../../productFilter/cards/components';
import { HubRelatedSite } from '../index';
import { useBuildSiteUrl } from '../../../../../../../hooks/useBuildSiteUrl';
import {
  getSitePartnersLogos,
  getSiteAttendanceLabel,
  getSiteCardDeliveryTypes,
  getSiteCardLanguages,
} from '../../../../helpers';

import styles from './RelatedSiteCard.module.scss';

export interface RelatedEventCardProps
  extends Omit<CardAnchorProps, 'to' | 'pathPrefix'> {
  data: HubRelatedSite;
}

export function RelatedSiteCard(props: RelatedEventCardProps) {
  const {
    data: {
      imageBackground,
      topics = [],
      name,
      dates = [],
      startDate,
      endDate,
      formattedDate,
      timeAsText,
      deliveryType,
      additionalDeliveryTypes = [],
      localisedDescription,
      brand: { colours: { secondary } = {} } = {},
      locations = [],
      languages = [],
      partners = [],
      sponsorLogo1,
      sponsorLogo2,
      contentType,
    },
    ...anchorProps
  } = props;
  const backgroundColor = secondary && `#${secondary}`;
  const url = useBuildSiteUrl(props.data);
  const date = {
    start: startDate,
    end: endDate,
    formatted: formattedDate,
  };
  const deliveryTypes = additionalDeliveryTypes.length
    ? additionalDeliveryTypes
    : [deliveryType];

  const sponsorsLogos = [sponsorLogo1, sponsorLogo2]
    .map((item) => item?.path)
    .filter(Boolean);
  const partnersLogos = getSitePartnersLogos(partners);
  const isPartners = partnersLogos.length > 0;
  const logos = isPartners ? partnersLogos : sponsorsLogos;
  const logosLabel = isPartners ? 'In association with:' : 'Sponsored By:';

  const topicsList = topics.filter((topic) => Boolean(topic));
  const languagesList = getSiteCardLanguages(languages);
  const deliveryTypesList = getSiteCardDeliveryTypes(deliveryTypes, locations);
  const buttonLabel = getSiteAttendanceLabel(
    deliveryTypes,
    contentType,
    dates.length > 0 ? dates[0].end : endDate,
  );

  return (
    <CardAnchor {...anchorProps} to={url}>
      <ProductCard
        image={<Image path={imageBackground} />}
        topics={
          topicsList.length > 0 && (
            <TopicsList list={topicsList.slice(0, 1)}>
              {(topic) => (
                <Badge theme="outlined" size="tiny" className={styles.topic}>
                  {topic}
                </Badge>
              )}
            </TopicsList>
          )
        }
        title={<Title size="extraSmall">{name}</Title>}
        date={
          <Date
            dates={dates.length ? dates : [date]}
            deliveryTypes={deliveryTypes}
            timeAsText={timeAsText}
          />
        }
        language={
          <Badge>
            <TextListSpacer list={languagesList} />
          </Badge>
        }
        showMoreBadge={buttonLabel && <CardButton>{buttonLabel}</CardButton>}
        sponsors={
          logos.length > 0 && <SponsorsList logos={logos} label={logosLabel} />
        }
        description={
          localisedDescription && (
            <Badge className={styles.description}>{localisedDescription}</Badge>
          )
        }
        deliveryTypes={
          deliveryTypesList.length > 0 && (
            <Badge className={styles.deliveryTypes}>
              <TextListSpacer list={deliveryTypesList} />
            </Badge>
          )
        }
        leftFooter={buttonLabel && <CardButton>{buttonLabel}</CardButton>}
        backgroundColor={backgroundColor}
        className={styles.card}
      />
    </CardAnchor>
  );
}
